import { Role, ItineraryStatus, ProcedureStatus, WaypointActionType } from '@caresend/types';
import { nullishFilter, hasStatus, formatCamelCase } from '@caresend/utils';
import { defineComponent, computed } from 'vue';
import __vue_component__ from '../../../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$2 from '../ProcedureInfoStatusPopup.vue.js';
import { useProcedureCancelationAlert, cancelProcedure } from './procedureCancelation.js';
import __vue_component__$5 from '../../../Workflow/InlineWorkflowItem.vue.js';
import { usePortalStore } from '../../../../store/portal/index.js';
import __vue_component__$3 from '../../../../../ProcedureDetails/ProcedureCancelationAlert/ProcedureCancelationAlert.vue.js';
import __vue_component__$1 from '../../../../../../ui/Icon.vue.js';
import { TagStyle } from '../../../../../../ui/Tag/model.js';
import __vue_component__$6 from '../../../../../../ui/Tag/Tag.vue.js';
import __vue_component__$4 from '../../../../../../ui/Tooltip/Tooltip.vue.js';
import { waypointRequiresFaxSending } from '../../../../../../../functions/itinerary/tempHelpers.js';
import 'immutability-helper';
import { getRouter } from '../../../../../../../router/helpers/initRouter.js';
import '../../../../../../../router/routes/dev/model.js';
import '../../../../../../../router/routes/dev/routes.js';
import { procedureRoute } from '../../../../../../../router/routes/locations.js';
import '../../../../../../../router/routes/orderFlow/model.js';
import '../../../../../../../router/routes/orderFlow/privateHelpers.js';
import '../../../../../../../router/routes/orderFlow/routes.js';
import '../../../../../../../router/routes/schedulingFlow/model.js';
import '../../../../../../../router/routes/schedulingFlow/routes.js';
import '../../../../../../../router/routes/chartingFlow/model.js';
import '../../../../../../../router/routes/chartingFlow/routes.js';
import '../../../../../../../router/routes/portal/portalRoutes.js';
import '../../../../../../../router/routes/portal/model.js';
import '../../../../../../../router/routes/pro/model.js';
import '../../../../../../../router/routes/visitsPage/model.js';
import '../../../../../../../router/routes/visitsPage/routes.js';
import { getStore } from '../../../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'WaypointProcedureCard',
    components: {
        ButtonComponent: __vue_component__,
        Icon: __vue_component__$1,
        ProcedureInfoStatusPopup: __vue_component__$2,
        ProcedureCancelationAlert: __vue_component__$3,
        Tooltip: __vue_component__$4,
        InlineWorkflow: __vue_component__$5,
        Tag: __vue_component__$6,
    },
    props: {
        inputDisabled: {
            type: Boolean,
        },
        procedureIDs: {
            type: Array,
            default: () => [],
        },
        waypointID: {
            type: String,
            default: undefined,
        },
        waypointActionID: {
            type: String,
            default: undefined,
        },
    },
    setup(props) {
        const router = getRouter();
        const store = getStore();
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const supplies = computed(() => props.procedureIDs.map((procedureID) => { var _a, _b; return (_b = (_a = portalStore.procedures.get(procedureID)) === null || _a === void 0 ? void 0 : _a.computed.supplyConfigItems) !== null && _b !== void 0 ? _b : null; }).filter(nullishFilter).flat());
        const procedures = computed(() => props.procedureIDs.map((procedureID) => portalStore.procedures.get(procedureID)).filter(nullishFilter));
        const waypoint = computed(() => { var _a; return props.waypointID ? (_a = portalStore.waypoints.getRaw(props.waypointID)) !== null && _a !== void 0 ? _a : undefined : undefined; });
        const itineraryID = computed(() => { var _a; return props.waypointID ? (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.itineraryID : undefined; });
        const waypointStatus = computed(() => { var _a, _b; return (_b = (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.status; });
        const itinerary = computed(() => {
            var _a;
            return itineraryID.value
                ? (_a = portalStore.itineraries.get(itineraryID.value)) !== null && _a !== void 0 ? _a : undefined
                : undefined;
        });
        const isNurseUser = computed(() => { var _a; return ((_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.role) === Role.NURSE; });
        /** Nurses cannot see procedures once the itinerary is done */
        const isProceduresPopupDisabled = computed(() => viewAs.assistant
            || (isNurseUser.value && hasStatus(itinerary.value, [
                ItineraryStatus.CANCELED,
                ItineraryStatus.COMPLETED,
            ])));
        const faxNumbers = computed(() => props.procedureIDs.map((procedureID) => waypointRequiresFaxSending(props.waypointID, procedureID)).filter(nullishFilter));
        const faxTooltip = computed(() => !faxNumbers.value.length ? undefined : faxNumbers.value.join(', '));
        const procedureStatuses = computed(() => {
            /** get all unique procedure statuses */
            const uniqueStatuses = new Map();
            procedures.value.map((procedure) => procedure.status).forEach((status) => {
                var _a;
                uniqueStatuses.set(status, ((_a = uniqueStatuses.get(status)) !== null && _a !== void 0 ? _a : 0) + 1);
            });
            return Array.from(uniqueStatuses).map(([status, count]) => {
                switch (status) {
                    case (ProcedureStatus.CREATED): {
                        return {
                            tagStyle: TagStyle['3_BLUE'],
                            friendlyStatus: 'pending',
                            count,
                        };
                    }
                    case (ProcedureStatus.CANCELED): {
                        return {
                            tagStyle: TagStyle['3_RED'],
                            friendlyStatus: 'canceled',
                            count,
                        };
                    }
                    case (ProcedureStatus.FAILED): {
                        return {
                            tagStyle: TagStyle['3_RED'],
                            friendlyStatus: formatCamelCase(status),
                            count,
                        };
                    }
                    case (ProcedureStatus.COMPLETED): {
                        return {
                            tagStyle: TagStyle['3_GREEN'],
                            friendlyStatus: formatCamelCase(status),
                            count,
                        };
                    }
                    case (ProcedureStatus.IN_PROGRESS): {
                        return {
                            tagStyle: TagStyle['3_ORANGE'],
                            friendlyStatus: formatCamelCase(status),
                            count,
                        };
                    }
                    case (ProcedureStatus.DRAFT): {
                        return {
                            tagStyle: TagStyle['3_BLUE'],
                            friendlyStatus: 'draft',
                            count,
                        };
                    }
                }
            });
        });
        const waypointAction = computed(() => {
            var _a;
            return props.waypointActionID
                ? (_a = portalStore.waypointActions.get(props.waypointActionID)) !== null && _a !== void 0 ? _a : undefined
                : undefined;
        });
        const productName = computed(() => Array.from(new Set(procedures.value
            .map((procedure) => store.getters['procedures/getProcedureProductInfo'](procedure.id))
            .map((productInfo) => { var _a; return !productInfo ? null : (_a = productInfo === null || productInfo === void 0 ? void 0 : productInfo.friendlyName) !== null && _a !== void 0 ? _a : 'Lab draw'; })).values())
            .filter(nullishFilter)
            .join(', '));
        /** get unique minimum titles */
        const minimumTitle = computed(() => Array.from(new Set(procedures.value.map((procedure) => store.getters['variables/getMinimumTitleByTaskTypeID'](procedure.taskTypeID, true))).values()).filter(nullishFilter)
            .join(', '));
        const isPatientAction = computed(() => { var _a; return ((_a = waypointAction.value) === null || _a === void 0 ? void 0 : _a.type) === WaypointActionType.PATIENT_ACTION; });
        const openProcedurePage = () => {
            const procedure = procedures.value[0];
            if (!(procedure === null || procedure === void 0 ? void 0 : procedure.id))
                return;
            const routeData = router.resolve(procedureRoute({ params: {
                    procedureID: procedure.id,
                } }));
            window.open(routeData.href, '_blank');
        };
        const isMinimizedView = computed(() => props.procedureIDs.length > 1);
        const singleProcedure = computed(() => (!isMinimizedView.value && procedures.value[0]) || undefined);
        const isOrphanDropOffOrMailIn = computed(() => singleProcedure.value
            ? portalStore.itinerary.getters.procedureIsDropOffOrphaned(singleProcedure.value.id)
            : false);
        const containerClasses = computed(() => {
            const classes = ['WaypointProcedureCard'];
            if (isOrphanDropOffOrMailIn.value)
                classes.push('WaypointProcedureCard--orphan');
            return classes;
        });
        const tooltipLabel = computed(() => {
            if (isOrphanDropOffOrMailIn.value)
                return 'This procedure was not found on any patient action';
        });
        const workflows = computed(() => {
            var _a, _b;
            return singleProcedure.value
                ? (_b = portalStore.procedures.getters.successWorkflows(singleProcedure.value.id)[(_a = props.waypointActionID) !== null && _a !== void 0 ? _a : '']) !== null && _b !== void 0 ? _b : {}
                : {};
        });
        const getWorkflowContextKey = (context) => { var _a; return `workflow-${props.waypointActionID}-${context}-${(_a = singleProcedure.value) === null || _a === void 0 ? void 0 : _a.id}`; };
        return {
            ...useProcedureCancelationAlert(),
            ProcedureStatus,
            cancelProcedure,
            containerClasses,
            faxTooltip,
            getWorkflowContextKey,
            isMinimizedView,
            isOrphanDropOffOrMailIn,
            isPatientAction,
            isProceduresPopupDisabled,
            itineraryID,
            minimumTitle,
            openProcedurePage,
            procedureStatuses,
            procedures,
            productName,
            singleProcedure,
            supplies,
            tooltipLabel,
            viewAs,
            waypointStatus,
            workflows,
        };
    },
});

export { script as default };
